import "../styling/PaymentMethodsStyles.css";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "../../../api/api";
import { useCheckout } from "../../../context/CheckoutContext";
import { validateBrazilianDocument } from "../../../utils/validationUtils";
import { useTranslation } from "../../../hooks/useTranslation";
import { formatFrequencyUnit } from "../../../utils/commonUtils";
import PixPaymentModal from "../../modal/PixPaymentModal";
import { fetchSession, updateSession } from "../../../utils/paymentSession";
import { fetchGatewayConnection, processPayment } from "../../../api/api";
import DlocalPixFormView from "./DlocalPixFormView";
import useWebSocket from "../../../hooks/useWebSocket";
import { toast } from "react-toastify";
import { formatPaymentError } from "../../../utils/formatPaymentError";
import { handleCitPaymentApiResponse, applyCitDeclineOrErrorUi, CIT_OUTCOME } from "../../../utils/paymentOutcome";
import useResumePendingPayment from "../../../hooks/useResumePendingPayment";

const DlocalPixForm = ({
  formData,
  itemDetails,
  billingPlatformId,
  routingEntries,
  planId,
  platformItemDetails,
  checkoutPageConfDto,
  checkoutProcessDto,

  // Props for Altruon variant
  onDocumentChange,
  onPaymentSuccess,
  isEmbeddedCheckout = false,
  onParentCallback,
  sessionId
}) => {
  const [documentNumber, setDocumentNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [pixResponse, setPixResponse] = useState(null);
  const [paymentSession, setPaymentSession] = useState(null);
  const { t } = useTranslation();
  const {
    triggerPaymentMethodEvent,
    resetTrigger,
    clientIp,
    clientCountry,
    paymentMethodName,
    platformName,
    estimatedOrder,
    isHostedPageMode,
  } = useCheckout();
  const transactionId = pixResponse?.data?.newTransactionId ||pixResponse?.data?.transactionId;

  const pixDlocalRouting = useMemo(() => {
    return routingEntries?.find(
      (entry) => entry.paymentMethod === "pix" && entry.platform === "dlocal"
    );
  }, [routingEntries]);

  const handleDocumentChange = useCallback(
    (e) => {
      const value = e.target.value.toUpperCase();
      setDocumentNumber(value);
      setDocumentError("");
      onDocumentChange?.(value);
    },
    [onDocumentChange]
  );

  const buildPayload = (gatewayConnection, latestSession) => ({
    paymentData: {
      gatewayConnectionId: gatewayConnection?.id,
      gatewayName: gatewayConnection?.platform,
      paymentMethod: "pix",
      // amount: itemDetails?.price || latestSession?.paymentData?.amount || 1000,
      // currency: itemDetails?.currencyCode || latestSession?.paymentData?.currency || "BRL",
      transactionId: "",
      additionalData: {
        payment_method_flow: "DIRECT",
        document: documentNumber,
      },
    },
    customerData: {
      firstName: formData?.firstName || latestSession?.customerData?.firstName,
      lastName: formData?.lastName || latestSession?.customerData?.lastName,
      email: formData?.email || latestSession?.customerData?.email,
      ip: clientIp || "192.0.126.111",
      billingAddress: {
        country: formData?.billingAddress?.country || latestSession?.customerData?.billingAddress?.country || clientCountry || "US",
        zipCode: formData?.billingAddress?.zipCode || latestSession?.customerData?.billingAddress?.zipCode || "90210",
        street: formData?.billingAddress?.street || latestSession?.customerData?.billingAddress?.street || "",
        city: formData?.billingAddress?.city || latestSession?.customerData?.billingAddress?.city || "",
        state: formData?.billingAddress?.state || latestSession?.customerData?.billingAddress?.state || "",
        number: "1234",
      },
    },
    billingData: {
      planId: platformItemDetails?.[0]?.id || planId,
      billingPlatformId,
      billingPlatformName: itemDetails?.billingPlatformName || latestSession?.billingData?.billingPlatformName || "",
      frequency: latestSession?.billingData?.frequency || "",
    },
  });


  const handleProcessPayment = useCallback(async (payload) => {
    if (isEmbeddedCheckout) {
      onParentCallback?.({
        type: "finalizeFormData",
        data: { paymentMethod: "pix", gatewayName: "dlocal", documentNumber }
      });
      await new Promise((r) => setTimeout(r, 800));
    }

    if (!isEmbeddedCheckout) {
      await updateSession(sessionId, payload);
    }

    return processPayment(billingPlatformId, sessionId, payload);
  }, [billingPlatformId, documentNumber, isEmbeddedCheckout, onParentCallback, sessionId]);

  const handlePayNow = useCallback(async () => {
    const isValidDocument = validateBrazilianDocument(documentNumber);

    if (!isValidDocument) {
      setDocumentError(t("paymentForms.pix.invalidDocument"));
      return;
    }

    if (!pixDlocalRouting) {
      setError(t("paymentForms.pix.unavailable"));
      return;
    }

    setLoading(true);

    try {
      const gateway_connection = await fetchGatewayConnection(pixDlocalRouting, t);
      const latestSession = await fetchSession(sessionId);
      
      // await new Promise(resolve => setTimeout(resolve, 800));
      const payload = buildPayload(gateway_connection, latestSession);
      if (!payload) {
        setLoading(false);
        return;
      }
      const res = await handleProcessPayment(payload);
      const citUi = { isHostedPageMode, isEmbeddedCheckout, t, setError, onParentCallback, toast };
      const { handled } = handleCitPaymentApiResponse(res, citUi);
      if (handled) return;

      const redirectUrl = res?.data?.redirect_url && `${res.data.redirect_url}?trxId=${res.data.newTransactionId}`;

      if (res.status === 200) {
        if (isEmbeddedCheckout) {
          setPaymentSession(latestSession);
          setPixResponse(res);
          onPaymentSuccess?.(res, latestSession);
          if (redirectUrl) {
            onParentCallback?.({
              type: "actionRequired",
              action: "REDIRECT",
              url: redirectUrl,
            });
            if (res?.data?.qrCodeUrl) {
              onParentCallback?.({
                type: "qrCodeRequired",
                qrData: res.data.qrCodeUrl,
              });
            }
          } else {
            onParentCallback?.({
              type: "success",
              transactionId: res.data.transactionId
            });
          }
        } else {
          setPaymentSession(latestSession);
          setPixResponse(res);
        }
      } else {
        if (isEmbeddedCheckout) {
          onParentCallback?.({ type: "failure", message: t("messages.error.somethingWrong") || "Transaction failed." });
        }
        throw new Error(t("messages.error.somethingWrong"));
      }
    } catch (e) {
      console.error("PIX Payment failed:", e);
      const citUi = { isHostedPageMode, isEmbeddedCheckout, t, setError, onParentCallback, toast };
      if (e?.response && handleCitPaymentApiResponse(e.response, citUi).handled) return;
      applyCitDeclineOrErrorUi(
        { outcome: CIT_OUTCOME.ERROR, message: formatPaymentError(e?.message, t) },
        citUi
      );
    } finally {
      setLoading(false);
    }
  }, [
    documentNumber,
    pixDlocalRouting,
    fetchGatewayConnection,
    handleProcessPayment,
    isEmbeddedCheckout,
    onParentCallback,
    t,
  ]);

  const handleResumePix = useCallback(
    async (paymentResponse, trxId) => {
      if (!sessionId) return;
      const latestSession = await fetchSession(sessionId);
      setPaymentSession(latestSession);
      setPixResponse({ data: paymentResponse });
    },
    [sessionId]
  );

  useResumePendingPayment({
    paymentMethod: "pix",
    platform: "dlocal",
    consumed: isEmbeddedCheckout || pixResponse != null,
    onResumePix: handleResumePix,
  });

  useEffect(() => {
    if (
      triggerPaymentMethodEvent && (isEmbeddedCheckout || (paymentMethodName === "pix" && platformName === "dlocal"))) {
      handlePayNow();
      resetTrigger();
    }
  }, [
    triggerPaymentMethodEvent,
    paymentMethodName,
    platformName,
    handlePayNow,
    resetTrigger,
  ]);


  // Listen websocket only for SDK version
  useWebSocket(isEmbeddedCheckout && transactionId, (event) => {
    const data = JSON.parse(event.data); 
      
      if (!data?.status) return;

      if (data.status === "success") {
        onParentCallback?.({
          type: "success",
          transactionId,
        });
      }

      if (data.status === "failed") {
        onParentCallback?.({
          type: "failure",
        });
      }
    },
    undefined,
    undefined,
    (err) => {
      console.error("PIX WS error", err);
    }
  );

  return (
    <DlocalPixFormView
      t={t}
      documentNumber={documentNumber}
      loading={loading}
      error={error}
      documentError={documentError}
      pixResponse={pixResponse}
      isEmbeddedCheckout={isEmbeddedCheckout}
      itemDetails={itemDetails}
      checkoutPageConfDto={checkoutPageConfDto}
      latestSession={paymentSession || checkoutProcessDto}
      onDocumentChange={handleDocumentChange}
    />
  );
};

export default DlocalPixForm;